import React, { useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminModerationAI.css';

const PROVIDER_LABELS = {
    openai_compatible: 'OpenAI-совместимый API',
    gigachat: 'GigaChat (настройки в .env)',
    disabled: 'Отключена — только фильтр запрещённых слов',
};

const URL_PRESETS = [
    { label: 'OpenAI', url: 'https://api.openai.com/v1' },
    { label: 'Cloud.ru Foundation Models', url: 'https://foundation-models.api.cloud.ru/v1' },
    { label: 'Yandex Cloud (OpenAI API)', url: 'https://llm.api.cloud.yandex.net/v1' },
    { label: 'OpenRouter', url: 'https://openrouter.ai/api/v1' },
    { label: 'DeepSeek', url: 'https://api.deepseek.com/v1' },
    { label: 'Ollama (локально)', url: 'http://localhost:11434/v1' },
    { label: 'LM Studio (локально)', url: 'http://localhost:1234/v1' },
];

const SOURCE_LABELS = {
    db: 'задано в админке',
    env: 'из .env',
    default: 'по умолчанию',
};

const DECISION_LABELS = {
    approved: 'одобрен',
    rejected: 'отклонён',
};

const EDITABLE_FIELDS = ['provider', 'base_url', 'model', 'verify_ssl', 'timeout_sec'];

function formFromConfig(config) {
    return {
        provider: config.provider,
        base_url: config.base_url || '',
        model: config.model || '',
        verify_ssl: config.verify_ssl !== false,
        timeout_sec: String(config.timeout_sec ?? 20),
    };
}

function SourceBadge({ source }) {
    if (!source) return null;
    return <span className={`mai-source mai-source--${source}`}>{SOURCE_LABELS[source] || source}</span>;
}

function AdminModerationAI() {
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState(null);
    const [apiKey, setApiKey] = useState('');
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);

    const [models, setModels] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [modelsError, setModelsError] = useState(null);

    const [sampleText, setSampleText] = useState('отличная лекция, спасибо');
    const [strictness, setStrictness] = useState('medium');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const applyConfig = (data) => {
        setConfig(data);
        setForm(formFromConfig(data));
        setApiKey('');
    };

    useEffect(() => {
        api.get('/admin/moderation-ai')
            .then((res) => applyConfig(res.data))
            .catch((err) => setLoadError(getApiErrorMessage(err, 'Не удалось загрузить настройки ИИ-модерации')));
    }, []);

    const initialForm = useMemo(() => (config ? formFromConfig(config) : null), [config]);
    const changedFields = useMemo(() => {
        if (!form || !initialForm) return [];
        return EDITABLE_FIELDS.filter((name) => form[name] !== initialForm[name]);
    }, [form, initialForm]);
    const isDirty = changedFields.length > 0 || apiKey.trim() !== '';

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setNotice(null);
    };

    // Значения формы, которые ещё не сохранены — для проверки и списка моделей.
    const formOverrides = () => ({
        provider: form.provider,
        base_url: form.base_url,
        model: form.model,
        verify_ssl: form.verify_ssl,
        timeout_sec: Number(form.timeout_sec) || undefined,
        api_key: apiKey.trim() || undefined,
    });

    const handleSave = async () => {
        const payload = {};
        changedFields.forEach((name) => {
            payload[name] = name === 'timeout_sec' ? (Number(form.timeout_sec) || null) : form[name];
        });
        if (apiKey.trim()) payload.api_key = apiKey.trim();
        setSaving(true);
        try {
            const res = await api.put('/admin/moderation-ai', payload);
            applyConfig(res.data);
            setNotice({ type: 'success', text: 'Настройки сохранены и уже применяются к новым ответам.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить настройки') });
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveKey = async () => {
        if (!window.confirm('Удалить API-ключ, сохранённый в админке? Будет использоваться ключ из .env, если он задан.')) return;
        setSaving(true);
        try {
            const res = await api.put('/admin/moderation-ai', { api_key: '' });
            applyConfig(res.data);
            setNotice({ type: 'success', text: 'Ключ удалён из админки.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось удалить ключ') });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Сбросить все настройки ИИ-модерации, заданные в админке? Будут использоваться значения из .env.')) return;
        setSaving(true);
        try {
            const res = await api.delete('/admin/moderation-ai');
            applyConfig(res.data);
            setTestResult(null);
            setNotice({ type: 'success', text: 'Настройки сброшены к значениям из .env.' });
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сбросить настройки') });
        } finally {
            setSaving(false);
        }
    };

    const handleLoadModels = async () => {
        setModelsLoading(true);
        setModelsError(null);
        try {
            const res = await api.post('/admin/moderation-ai/models', formOverrides());
            const list = Array.isArray(res.data.models) ? res.data.models : [];
            setModels(list);
            if (!list.length) setModelsError('API вернул пустой список моделей');
        } catch (err) {
            setModels([]);
            setModelsError(getApiErrorMessage(err, 'Не удалось получить список моделей'));
        } finally {
            setModelsLoading(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await api.post('/admin/moderation-ai/test', {
                ...formOverrides(),
                sample_text: sampleText,
                strictness,
            });
            setTestResult(res.data);
        } catch (err) {
            setTestResult({ ok: false, error: getApiErrorMessage(err, 'Ошибка проверки') });
        } finally {
            setTesting(false);
        }
    };

    if (loadError) {
        return (
            <section className="admin-main-card">
                <h2 className="admin-main-card__title">ИИ-модерация</h2>
                <div className="mai-body"><div className="mai-alert mai-alert--error">{loadError}</div></div>
            </section>
        );
    }

    if (!config || !form) {
        return (
            <section className="admin-main-card">
                <h2 className="admin-main-card__title">ИИ-модерация</h2>
                <div className="mai-body">Загрузка…</div>
            </section>
        );
    }

    const isOpenAI = form.provider === 'openai_compatible';
    const isGigaChat = form.provider === 'gigachat';
    const keySource = config.sources?.api_key;

    return (
        <section className="admin-main-card">
            <h2 className="admin-main-card__title">ИИ-модерация облака слов</h2>
            <div className="mai-body">
                <p className="mai-hint">
                    Применяется к вопросам «Облако слов» с включённой премодерацией. Если модель недоступна
                    или ответила с ошибкой, ответ проверяется фильтром запрещённых слов.
                </p>

                <div className={`mai-status ${config.is_configured ? 'mai-status--ok' : 'mai-status--off'}`}>
                    <strong>Сейчас:</strong>{' '}
                    {PROVIDER_LABELS[config.provider]}
                    {config.provider === 'openai_compatible' && config.model ? ` · ${config.model}` : ''}
                    {config.provider === 'gigachat' ? ` · ${config.gigachat_model}` : ''}
                    {config.provider !== 'disabled' && !config.is_configured ? ' — не настроено, работает только фильтр слов' : ''}
                </div>

                <div className="form-group">
                    <label htmlFor="mai-provider">Провайдер <SourceBadge source={config.sources?.provider} /></label>
                    <select id="mai-provider" value={form.provider} onChange={(e) => setField('provider', e.target.value)}>
                        {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>

                {isOpenAI && (
                    <>
                        <div className="form-group">
                            <label htmlFor="mai-base-url">Адрес API (base URL) <SourceBadge source={config.sources?.base_url} /></label>
                            <div className="mai-row">
                                <input
                                    id="mai-base-url"
                                    type="url"
                                    placeholder="https://api.openai.com/v1"
                                    value={form.base_url}
                                    onChange={(e) => setField('base_url', e.target.value)}
                                />
                                <select
                                    className="mai-preset"
                                    value=""
                                    onChange={(e) => e.target.value && setField('base_url', e.target.value)}
                                    aria-label="Подставить адрес известного провайдера"
                                >
                                    <option value="">Шаблоны…</option>
                                    {URL_PRESETS.map((p) => (
                                        <option key={p.url} value={p.url}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                            <p className="mai-field-hint">Адрес до <code>/chat/completions</code>, обычно оканчивается на <code>/v1</code>.</p>
                        </div>

                        <div className="form-group">
                            <label htmlFor="mai-api-key">API-ключ <SourceBadge source={config.api_key_set ? keySource : null} /></label>
                            <div className="mai-row">
                                <input
                                    id="mai-api-key"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder={config.api_key_set
                                        ? `Сохранён: ${config.api_key_masked}. Оставьте пустым, чтобы не менять`
                                        : 'Не задан (для локальных моделей может не требоваться)'}
                                    value={apiKey}
                                    onChange={(e) => { setApiKey(e.target.value); setNotice(null); }}
                                />
                                {keySource === 'db' && (
                                    <button type="button" className="btn btn--danger-outline btn--sm" onClick={handleRemoveKey} disabled={saving}>
                                        Удалить
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="mai-model">Модель <SourceBadge source={config.sources?.model} /></label>
                            <div className="mai-row">
                                <input
                                    id="mai-model"
                                    type="text"
                                    list="mai-model-list"
                                    placeholder="например, gpt-4o-mini"
                                    value={form.model}
                                    onChange={(e) => setField('model', e.target.value)}
                                />
                                <button type="button" className="btn btn--secondary btn--sm" onClick={handleLoadModels} disabled={modelsLoading || !form.base_url}>
                                    {modelsLoading ? 'Загрузка…' : 'Список моделей'}
                                </button>
                            </div>
                            <datalist id="mai-model-list">
                                {models.map((id) => <option key={id} value={id} />)}
                            </datalist>
                            {models.length > 0 && (
                                <p className="mai-field-hint">Найдено моделей: {models.length}. Начните вводить название, чтобы выбрать из списка.</p>
                            )}
                            {modelsError && <p className="mai-field-hint mai-field-hint--error">{modelsError}</p>}
                        </div>

                        <div className="mai-grid">
                            <div className="form-group">
                                <label htmlFor="mai-timeout">Таймаут, сек <SourceBadge source={config.sources?.timeout_sec} /></label>
                                <input
                                    id="mai-timeout"
                                    type="number"
                                    min="3"
                                    max="120"
                                    value={form.timeout_sec}
                                    onChange={(e) => setField('timeout_sec', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="mai-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={form.verify_ssl}
                                        onChange={(e) => setField('verify_ssl', e.target.checked)}
                                    />
                                    Проверять SSL-сертификат <SourceBadge source={config.sources?.verify_ssl} />
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {isGigaChat && (
                    <div className={`mai-alert ${config.gigachat_configured ? 'mai-alert--info' : 'mai-alert--error'}`}>
                        {config.gigachat_configured
                            ? `GigaChat настроен через .env (модель ${config.gigachat_model}).`
                            : 'Учётные данные GigaChat не заданы. Укажите GIGACHAT_CLIENT_ID и GIGACHAT_CLIENT_SECRET (или GIGACHAT_AUTHORIZATION_BASIC) в .env и перезапустите backend.'}
                    </div>
                )}

                {notice && <div className={`mai-alert mai-alert--${notice.type}`}>{notice.text}</div>}

                <div className="mai-actions">
                    <button type="button" className="btn btn--success" onClick={handleSave} disabled={saving || !isDirty}>
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                    <button type="button" className="btn btn--secondary" onClick={handleReset} disabled={saving}>
                        Сбросить к .env
                    </button>
                </div>

                {form.provider !== 'disabled' && (
                    <div className="mai-test">
                        <h3 className="mai-test__title">Проверка подключения</h3>
                        <p className="mai-hint">
                            Отправляет пробный ответ в модель с настройками из формы — сохранять перед проверкой не обязательно.
                        </p>
                        <div className="mai-grid mai-grid--test">
                            <div className="form-group">
                                <label htmlFor="mai-sample">Текст ответа</label>
                                <input id="mai-sample" type="text" maxLength={500} value={sampleText} onChange={(e) => setSampleText(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="mai-strictness">Строгость</label>
                                <select id="mai-strictness" value={strictness} onChange={(e) => setStrictness(e.target.value)}>
                                    <option value="low">Низкая</option>
                                    <option value="medium">Средняя</option>
                                    <option value="high">Высокая</option>
                                </select>
                            </div>
                        </div>
                        <button type="button" className="btn btn--primary" onClick={handleTest} disabled={testing}>
                            {testing ? 'Проверяем…' : 'Проверить'}
                        </button>

                        {testResult && (
                            <div className={`mai-alert ${testResult.ok ? 'mai-alert--success' : 'mai-alert--error'} mai-test__result`} role="status">
                                {testResult.ok ? (
                                    <>
                                        <strong>Модель работает</strong>
                                        {testResult.latency_ms != null && <> · ответ за {testResult.latency_ms} мс</>}
                                        <div>
                                            Решение: <strong>{DECISION_LABELS[testResult.decision] || testResult.decision}</strong>
                                            {testResult.score != null && <>, оценка нарушения {testResult.score.toFixed(2)}</>}
                                            {testResult.reason && <>, причина: {testResult.reason}</>}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <strong>Не работает</strong>
                                        {testResult.latency_ms != null && <> · {testResult.latency_ms} мс</>}
                                        <div className="mai-test__error">{testResult.error}</div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export default AdminModerationAI;
